import React, {useMemo, useState} from 'react';
import {ComboBox, Text} from '@forecast-it/design-system';
import {
	getCompanyRoles,
	getDisabledRoleIds,
	getRoleWarningTagOrUndefined,
} from '../../../modals/time-registration/time-registration-modal/util/TimeRegistrationModalRoleUtil';
import {Role} from '../../../modals/time-registration/time-registration-modal/types/TimeRegistrationRole';
import {TRACKING_OBJECTS} from '../../../../../../tracking/amplitude/constants/TrackingObjects';
import {trackEvent} from '../../../../../../tracking/amplitude/TrackingV2';
import {createFragmentContainer, graphql} from 'react-relay';
import type {SkylineRoleDropdown_viewer$data as SkylineRoleDropdown_viewer} from './__generated__/SkylineRoleDropdown_viewer.graphql';
import {FormattedHTMLMessage} from 'react-intl';

function getErrorText(roleId: string | undefined, roles: Role[]) {
	const roleWarningTag = getRoleWarningTagOrUndefined(roleId, roles);
	return roleWarningTag ? <FormattedHTMLMessage id="project_person.deactivated_role" /> : undefined;
}

type SkylineRoleDropdownRelayProps = {
	viewer: SkylineRoleDropdown_viewer;
};

export type SkylineRoleDropdownComponentProps = {
	role?: Role;
	onSelect: (role?: Role) => void;
	controlled?: boolean;
	projectId?: string;
};

type SkylineRoleDropdownProps = SkylineRoleDropdownRelayProps & SkylineRoleDropdownComponentProps;

const SkylineRoleDropdown = ({viewer, role, controlled, onSelect, projectId}: SkylineRoleDropdownProps): JSX.Element => {
	const [internalRole, setInternalRole] = useState<Role | undefined>(role);
	const roles = useMemo(() => getCompanyRoles(viewer.company?.roles, projectId, role), [viewer.company?.roles, projectId]);
	const roleErrorText = useMemo(
		() => getErrorText(controlled ? role?.id : internalRole?.id, roles),
		[controlled ? role?.id : internalRole?.id, roles]
	);
	function handleTimeRegRoleChange(roleId: string) {
		const newRole = roles.find(role => role.id === roleId);
		onSelect(newRole);
		if (!controlled) {
			setInternalRole(newRole);
		}
		trackEvent(TRACKING_OBJECTS.TIME_REGISTRATION_MODAL, 'Role Changed', {
			timeRegistrationRoleName: newRole?.name,
		});
	}

	return (
		<ComboBox
			data-cy={'role-combobox'}
			label={'Role'}
			defaultItems={roles}
			value={controlled ? role?.id : internalRole?.id}
			onChange={handleTimeRegRoleChange}
			width={'flex'}
			errorText={roleErrorText}
			showErrorAsIconTooltip={true}
			disabledKeys={getDisabledRoleIds(roles)}
		>
			{item => (
				<ComboBox.Item textValue={item.name || ''}>
					<Text noWrap={true}>{item.name}</Text>
				</ComboBox.Item>
			)}
		</ComboBox>
	);
};
const SkylineRoleDropdownQuery = graphql`
	query SkylineRoleDropdown_Query($projectId: ID) {
		viewer {
			component(name: "role_dropdown")
			actualPersonId
			id
			...SkylineRoleDropdown_viewer @arguments(projectId: $projectId)
		}
	}
`;

export {SkylineRoleDropdownQuery};

export default createFragmentContainer(SkylineRoleDropdown, {
	viewer: graphql`
		fragment SkylineRoleDropdown_viewer on Viewer @argumentDefinitions(projectId: {type: "ID"}) {
			company {
				roles(includeDisabled: false, projectId: $projectId) {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	`,
});
